import { useEffect, useMemo, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  CARD_BUILTIN_FIELDS,
  getDefaultCardConfig,
  normalizeCardConfig,
} from '../../lib/events';
import { getLeafFields } from '../../lib/invitation';
import { Button } from '../ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import { ParticipantCard } from './StoreColumn';
import { CardConfigContext } from './CardConfigContext';

const DEMO_PARTICIPANT = {
  responseId: 'preview',
  name: 'Mario',
  surname: 'Rossi',
  phone: '333 1234567',
  email: 'mario.rossi@email.it',
  availabilitySlots: ['Sabato 09:00', 'Sabato 14:00', 'Domenica 09:00', 'Domenica 14:00'],
  extras: {},
};

function scoreParticipant(participant) {
  return (
    (participant.phone ? 1 : 0) +
    (participant.email ? 1 : 0) +
    (participant.availabilitySlots?.length ? 1 : 0) +
    Object.keys(participant.extras || {}).length
  );
}

function ToggleRow({ label, hint, checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={cn(
        'flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition-colors',
        'border-[var(--ba-surface-border)] hover:border-brand-orange/50',
        checked && 'border-brand-orange/40 bg-brand-orange/5'
      )}
    >
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium">{label}</span>
        {hint && <span className="block truncate text-[11px] ba-muted">{hint}</span>}
      </span>
      <span
        aria-hidden="true"
        className={cn(
          'relative h-5 w-9 shrink-0 rounded-full transition-colors',
          checked ? 'bg-brand-orange' : 'bg-[var(--ba-surface-border)]'
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all',
            checked ? 'left-[1.125rem]' : 'left-0.5'
          )}
        />
      </span>
    </button>
  );
}

function CardConfigSheet({
  open,
  onOpenChange,
  config,
  participants,
  invitationFields,
  fieldMapping,
  onApply,
}) {
  const [draft, setDraft] = useState(() => normalizeCardConfig(config));

  useEffect(() => {
    if (open) setDraft(normalizeCardConfig(config));
  }, [open, config]);

  const extraOptions = useMemo(() => {
    const mapped = new Set(Object.values(fieldMapping || {}).filter(Boolean));
    return getLeafFields(invitationFields)
      .filter((field) => field.name && !mapped.has(field.name))
      .map((field) => ({ key: `extra:${field.name}`, label: field.label || field.name }));
  }, [invitationFields, fieldMapping]);

  const previewParticipant = useMemo(() => {
    if (!participants?.length) return DEMO_PARTICIPANT;
    return participants.reduce((best, p) => (scoreParticipant(p) > scoreParticipant(best) ? p : best));
  }, [participants]);

  const enabled = new Set(draft.fields);
  const setField = (key, on) =>
    setDraft((prev) => ({
      ...prev,
      fields: on ? [...prev.fields, key] : prev.fields.filter((f) => f !== key),
    }));

  const defaults = getDefaultCardConfig();
  const isDefault =
    draft.fields.length === defaults.fields.length && defaults.fields.every((f) => enabled.has(f));
  const unchanged =
    JSON.stringify([...draft.fields].sort()) ===
    JSON.stringify([...normalizeCardConfig(config).fields].sort());

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="gap-0 p-0 sm:max-w-md">
        <SheetHeader className="border-b border-[var(--ba-surface-border)] p-4 pr-12">
          <SheetTitle>Personalizza card</SheetTitle>
          <SheetDescription>
            Scegli le informazioni mostrate sulle card dei partecipanti, in Colonne e Mappa.
          </SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
          <section aria-label="Anteprima">
            <p className="ba-label">Anteprima</p>
            <div className="rounded-xl border border-dashed border-[var(--ba-surface-border)] bg-black/[0.03] p-4 dark:bg-white/[0.03]">
              <div className="mx-auto w-full max-w-[280px]">
                <CardConfigContext.Provider value={draft}>
                  <ParticipantCard
                    participant={previewParticipant}
                    compact
                    showGrip
                    assignmentCount={2}
                  />
                </CardConfigContext.Provider>
              </div>
            </div>
            <p className="mt-1.5 text-[11px] ba-muted">
              {previewParticipant === DEMO_PARTICIPANT
                ? 'Esempio con dati di prova.'
                : 'Dati di un partecipante reale dell\'evento.'}
            </p>
          </section>

          <section aria-label="Informazioni mostrate" className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <p className="ba-label !mb-0">Informazioni mostrate</p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                disabled={isDefault}
                onClick={() => setDraft(getDefaultCardConfig())}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Ripristina
              </Button>
            </div>
            <div className="rounded-lg border border-[var(--ba-surface-border)] px-3 py-2">
              <p className="text-sm font-medium">Nome</p>
              <p className="text-[11px] ba-muted">Sempre visibile</p>
            </div>
            {CARD_BUILTIN_FIELDS.map((field) => (
              <ToggleRow
                key={field.key}
                label={field.label}
                checked={enabled.has(field.key)}
                onChange={(on) => setField(field.key, on)}
              />
            ))}
          </section>

          {extraOptions.length > 0 && (
            <section aria-label="Altri campi dell'invito" className="space-y-2">
              <p className="ba-label">Altri campi dell&apos;invito</p>
              {extraOptions.map((field) => (
                <ToggleRow
                  key={field.key}
                  label={field.label}
                  checked={enabled.has(field.key)}
                  onChange={(on) => setField(field.key, on)}
                />
              ))}
            </section>
          )}
        </div>

        <SheetFooter className="grid grid-cols-2 gap-2 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:flex sm:justify-end">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button
            type="button"
            disabled={unchanged}
            onClick={() => {
              onApply(draft);
              onOpenChange(false);
            }}
          >
            Applica
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

export default CardConfigSheet;
